import { Link } from 'wouter';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function BlogPost() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Article */}
      <article className="pt-20 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link href="/blog">
            <a className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold mb-8 transition-colors">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Blog
            </a>
          </Link>

          {/* Meta */}
          <div className="flex items-center gap-4 mb-6">
            <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-semibold">
              Product
            </span>
            <div className="flex items-center text-sm text-slate-500">
              <Calendar className="w-4 h-4 mr-1" />
              January 12, 2026
            </div>
            <div className="flex items-center text-sm text-slate-500">
              <Clock className="w-4 h-4 mr-1" />
              5 min read
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl font-bold text-slate-900 mb-6 leading-tight">
            Introducing dakkio.io: Time-Series Monitoring for Everyone
          </h1>

          {/* Author */}
          <div className="flex items-center mb-12 pb-8 border-b border-slate-200">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-secondary-500 rounded-full mr-4"></div>
            <div>
              <div className="font-semibold text-slate-900">Malex Alexandru</div>
              <div className="text-sm text-slate-600">Founder, dakkio.io</div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              After years of wrestling with complex monitoring tools while building BeePulse
              (our beehive monitoring platform), we realized something was fundamentally
              broken with how developers interact with time-series data.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">
              The Problem: Monitoring Shouldn't Require a PhD
            </h2>

            <p className="text-slate-700 leading-relaxed mb-6">
              Whether you're monitoring IoT sensors, infrastructure metrics, or business KPIs,
              the story is always the same:
            </p>

            <ul className="space-y-3 mb-8">
              <li className="text-slate-700">
                <strong>Complex Query Languages:</strong> Learning PromQL, InfluxQL, or SQL
                variants just to ask "what was the average temperature yesterday?"
              </li>
              <li className="text-slate-700">
                <strong>Dashboard Hell:</strong> Spending more time configuring dashboards than
                analyzing data
              </li>
              <li className="text-slate-700">
                <strong>Alert Fatigue:</strong> Writing complex rules that are either too
                sensitive or miss critical issues
              </li>
              <li className="text-slate-700">
                <strong>Integration Nightmares:</strong> Every tool has different APIs,
                authentication, and data formats
              </li>
            </ul>

            <p className="text-slate-700 leading-relaxed mb-6">
              We built dakkio because we believe <strong>monitoring should be as simple as
              talking to a colleague</strong>. If you can describe what you want in plain
              English, you should be able to get it—no manuals, no tutorials, no query
              languages.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">
              Natural Language: The Missing Interface
            </h2>

            <p className="text-slate-700 leading-relaxed mb-6">
              Imagine instead of writing:
            </p>

            <div className="bg-slate-900 rounded-lg p-6 mb-6 font-mono text-sm text-slate-100 overflow-x-auto">
              <code>
                SELECT avg(value) FROM sensors<br />
                WHERE type='temperature' AND value &gt; 30<br />
                GROUP BY time(5m) HAVING count(*) &gt; 2
              </code>
            </div>

            <p className="text-slate-700 leading-relaxed mb-6">
              You could just say:
            </p>

            <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-6 mb-8">
              <p className="text-slate-900 italic text-lg">
                "Alert me if temperature stays above 30°C for more than 10 minutes"
              </p>
            </div>

            <p className="text-slate-700 leading-relaxed mb-6">
              That's the core idea behind dakkio. Our AI understands what you want and
              translates it into the right queries, alerts, and visualizations—automatically.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">
              Built for Developers, Loved by Everyone
            </h2>

            <p className="text-slate-700 leading-relaxed mb-6">
              We designed dakkio with three core principles:
            </p>

            <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
              1. Natural Language First
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              While we provide a powerful REST API and SDKs for programmatic access, the
              primary interface is conversational. Create alerts, query data, and generate
              insights by describing what you need in plain English.
            </p>

            <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
              2. Developer Experience Matters
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              Fast APIs, comprehensive SDKs (TypeScript, Python, Go), embeddable widgets,
              and webhook integrations. We're developers too—we know what good DX looks like.
            </p>

            <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
              3. Transparency and Simplicity
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              No hidden costs, no vendor lock-in, no complex pricing tiers. Start free,
              pay only for what you use, and cancel anytime. Open source integrations and
              clear documentation.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">
              Real-World Use Case: BeePulse
            </h2>

            <p className="text-slate-700 leading-relaxed mb-6">
              dakkio was born from our own need at BeePulse. We monitor beehives with sensors
              tracking weight, temperature, humidity, and more. Before dakkio, we built a
              custom monitoring system that took months to develop and was a nightmare to
              maintain.
            </p>

            <p className="text-slate-700 leading-relaxed mb-6">
              Now, BeePulse runs entirely on dakkio:
            </p>

            <ul className="space-y-3 mb-8">
              <li className="text-slate-700">
                <strong>Data Ingestion:</strong> Sensors send data every 15 minutes via simple
                REST API
              </li>
              <li className="text-slate-700">
                <strong>Alerts:</strong> "Notify me if hive weight drops by more than 20% in a
                day" (honey theft detection!)
              </li>
              <li className="text-slate-700">
                <strong>Visualizations:</strong> Embeddable charts in customer dashboards
              </li>
              <li className="text-slate-700">
                <strong>Webhooks:</strong> Integrate with email, SMS, and in-app notifications
              </li>
            </ul>

            <p className="text-slate-700 leading-relaxed mb-6">
              We went from months of custom development to <strong>production in a
              weekend</strong>.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">
              What's Next?
            </h2>

            <p className="text-slate-700 leading-relaxed mb-6">
              We're launching dakkio in phases:
            </p>

            <ul className="space-y-3 mb-8">
              <li className="text-slate-700">
                <strong>Phase 1 (Now):</strong> Core data infrastructure, APIs, and
                authentication
              </li>
              <li className="text-slate-700">
                <strong>Phase 2 (Feb 2026):</strong> Webhook notifications and rule engine
              </li>
              <li className="text-slate-700">
                <strong>Phase 3 (Mar 2026):</strong> Embeddable widgets and customization
              </li>
              <li className="text-slate-700">
                <strong>Phase 4 (Apr 2026):</strong> Full natural language interface
              </li>
            </ul>

            <p className="text-slate-700 leading-relaxed mb-6">
              We're starting with the fundamentals—rock-solid data storage and querying—then
              adding the natural language magic on top. This ensures we build a stable
              foundation before introducing the more experimental features.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">
              Join Us
            </h2>

            <p className="text-slate-700 leading-relaxed mb-6">
              Whether you're monitoring IoT sensors, tracking infrastructure metrics, or
              building data-driven products, we'd love to have you try dakkio.
            </p>

            <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-8 mt-8 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Start Monitoring in Minutes
              </h3>
              <p className="text-slate-600 mb-6">
                Free tier available. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/signup">
                  <a className="px-8 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors">
                    Get Started Free
                  </a>
                </Link>
                <a
                  href="https://docs.dakkio.io"
                  className="px-8 py-3 bg-white border-2 border-slate-200 text-slate-900 rounded-lg font-semibold hover:border-slate-300 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read Documentation
                </a>
              </div>
            </div>

            <p className="text-slate-700 leading-relaxed mt-8">
              Questions? Feedback? We're <a href="mailto:hello@dakkio.io" className="text-primary-600 hover:text-primary-700 font-semibold">hello@dakkio.io</a> and
              on <a href="https://twitter.com/dakkio" className="text-primary-600 hover:text-primary-700 font-semibold" target="_blank" rel="noopener noreferrer">Twitter</a>.
            </p>

            <p className="text-slate-700 leading-relaxed mt-4">
              — Malex Alexandru, Founder
            </p>
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">Share this post:</div>
              <div className="flex gap-3">
                <a
                  href="https://twitter.com/intent/tweet?text=Introducing%20dakkio.io&url=https://dakkio.io/blog/introducing-dakkio"
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Twitter
                </a>
                <a
                  href="https://www.linkedin.com/sharing/share-offsite/?url=https://dakkio.io/blog/introducing-dakkio"
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
